import type { SerializedStyles } from "@emotion/react";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { Attachment, Typography } from "@aviary";

import * as styles from "./Dropzone.styles";

/**
 * TODO: Handle showing previews of specific file types i.e: images
 */

interface Props {
  file: File;
  supportedFileTypes: string;
  successMessage?: {
    heading: string;
    description: string;
  };
  onRemoveFile?: () => void;
  onUploadFile: (file: File) => void;
  onError?: () => void;
  children: ReactNode;
  previewUrl?: string;
  isAttachmentFullWidth?: boolean;
  customStyles?: SerializedStyles;
}

const Dropzone = ({
  file,
  supportedFileTypes,
  successMessage,
  onRemoveFile,
  onUploadFile,
  onError,
  children,
  previewUrl,
  isAttachmentFullWidth,
  customStyles,
}: Props) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (fileRejections?.length > 0 && onError) {
      onError();
    } else {
      onUploadFile(acceptedFiles[0]);
    }
  }, []);

  const dropzoneStyles = () => {
    return [styles.zoneText, isDragActive && styles.dropzoneIsDroppable];
  };

  const { isDragActive, getRootProps, getInputProps, fileRejections } = useDropzone({
    accept: supportedFileTypes,
    onDrop,
    multiple: false,
    useFsAccessApi: false,
  });

  if (file) {
    return (
      <div>
        <Attachment
          fileName={file.name}
          handleRemove={onRemoveFile}
          customIcon={!!previewUrl && <div css={styles.fileThumbnail(previewUrl)} />}
          css={styles.fileWrapper}
          data-testid="file-name"
          isFullWidth={isAttachmentFullWidth}
        />
        {successMessage && (
          <div css={styles.successBlock}>
            <Typography type="h5" isMarginless css={styles.successText}>
              {successMessage.heading}
            </Typography>
            <Typography>{successMessage.description}</Typography>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="dropzone" css={styles.dropzoneContainer}>
      <div {...getRootProps()} css={[dropzoneStyles(), customStyles ? customStyles : ""]}>
        <input data-testid="dropzone-input" data-e2e="dropzone-input" {...getInputProps()} />
        <div css={styles.readyContainer}>{children}</div>
      </div>
    </div>
  );
};

export { Dropzone };
